import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { generatePath, useHistory } from 'react-router-dom';

import type { IResourceDatabaseFolder } from '@/api/types';
import type { ISelectedFolder } from '@/store/types';

import { classList, compareSelectedFolder, isChildrenSelected } from '@/helpers';
import { useAppSelector, useResourceNavigation } from '@/hooks';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { TreeFolderAccordion } from '@/components/common';
import { FolderItemDatabaseView } from '.';
import routes from '@/config/routes';

interface IProps {
  data: IResourceDatabaseFolder;
  resourceDetails: {
    clientId: number;
    folderType: 'database';
  };
  eventKey?: number;
}

const folderItemDatabaseSelector = createSelector(selectFoldersList, foldersList => ({
  foldersList,
}));
const FolderItemDatabase = (props: IProps): React.ReactElement => {
  const { data, eventKey, resourceDetails } = props;
  const { selectedFolder } = useResourceNavigation();
  const { foldersList } = useAppSelector(folderItemDatabaseSelector);
  const history = useHistory();

  const folderSignature: ISelectedFolder = {
    clientId: resourceDetails.clientId,
    folderId: data.id,
    folderType: resourceDetails.folderType,
  };
  const isSelected = !!selectedFolder && compareSelectedFolder(selectedFolder, folderSignature);

  const handleOnSelect = () => {
    if (!isSelected) {
      history.push(
        generatePath(routes.sp.scheduling.resources.database, {
          client: resourceDetails.clientId,
          dbId: data.id,
        }),
      );
    }
  };

  return (
    <TreeFolderAccordion
      title={data.name}
      onSelectCallback={handleOnSelect}
      className={classList(isSelected && 'accordion-title-selected')}
      eventKey={eventKey}
      isChildrenSelected={
        selectedFolder ? isChildrenSelected(folderSignature, selectedFolder, foldersList || []) : false
      }
    >
      {(data.folders || []).map((view, id) => (
        <FolderItemDatabaseView
          key={`${view.rtxDatabase_Id}_${id}`}
          data={view}
          resourceDetails={{ ...resourceDetails }}
          eventKey={id}
        />
      ))}
    </TreeFolderAccordion>
  );
};

export { FolderItemDatabase };
